import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFeature } from "@/lib/early-access";
import { trackOnboarding } from "@/lib/guided/events";
import { ensurePracticeFlag } from "@/lib/guided/practice";
import {
  isPracticeFlagChecked,
  isPracticeTourDone,
  markPracticeFlagChecked,
  matchPracticeVisit,
  setPracticeTourDone,
} from "@/lib/guided/practice-visits";
import { savePracticeNote } from "@/lib/guided/writes";
import { countVerifyMarkers } from "@/lib/verifyMarkers";
import type { FileData } from "@/services/files";
import { useAuthStore } from "@/store/auth.store";
import { completeChecklistItem } from "@/store/guidedChecklist.store";

// A practice consulta on the visit page (guided first visit): recognised
// from the list registry, its metformin dose flagged once the note is
// written (if the model did not flag it, the marker is added here and saved,
// so the tour never depends on the model), and the guided tour's state.

export interface PracticeNote {
  practice: boolean;
  // The note is written and its flag is in place: the tour can run.
  ready: boolean;
  tourOpen: boolean;
  // The flagged value was confirmed or edited.
  reviewed: boolean;
  closeTour: () => void;
  restartTour: () => void;
}

const isWriting = (file: FileData): boolean =>
  file.status !== "Ready" || !file.result?.trim();

export const usePracticeNote = (
  file: FileData | null,
  refresh: () => void,
): PracticeNote => {
  const { t } = useTranslation();
  const account = useAuthStore((state) => state.userData?.public_id);
  const guided = useFeature("guided-first-visit");
  const practice = guided && !!file && matchPracticeVisit(account, file);
  const [flagReady, setFlagReady] = useState(false);
  const [tourDone, setTourDone] = useState(true);
  const running = useRef(false);
  // The flagged value was seen on this page, or handled on an earlier load:
  // a note without markers then means it was reviewed.
  const [sawFlag, setSawFlag] = useState(false);
  const [checkedEarlier, setCheckedEarlier] = useState(false);
  const visitId = file?.public_id ?? null;
  const writing = !file || isWriting(file);
  const result = file?.result ?? "";

  useEffect(() => {
    setFlagReady(false);
    setSawFlag(false);
    setCheckedEarlier(
      !!visitId && !!account && isPracticeFlagChecked(account, visitId),
    );
    running.current = false;
    setTourDone(
      !visitId || !account ? true : isPracticeTourDone(account, visitId),
    );
  }, [visitId, account]);

  useEffect(() => {
    if (!practice || !visitId || writing || running.current) return;
    if (isPracticeFlagChecked(account, visitId)) {
      setFlagReady(true);
      return;
    }
    running.current = true;
    trackOnboarding("practice_note_opened");
    completeChecklistItem("practice");
    const { text, added } = ensurePracticeFlag(
      result,
      t("guided.practice.flagFallback"),
    );
    if (!added) {
      markPracticeFlagChecked(account, visitId);
      setFlagReady(true);
      return;
    }
    void savePracticeNote(visitId, text)
      .then((saved) => {
        if (!saved) return;
        markPracticeFlagChecked(account, visitId);
        trackOnboarding("practice_flag_added");
        refresh();
      })
      .finally(() => {
        setFlagReady(true);
      });
  }, [practice, visitId, writing, result, account, t, refresh]);

  const flags = countVerifyMarkers(result);
  useEffect(() => {
    if (practice && flags > 0) setSawFlag(true);
  }, [practice, flags]);
  const reviewed = flags === 0 && (sawFlag || checkedEarlier);
  // Ready once the note shows its flag (or the flag was already reviewed).
  const ready = practice && flagReady && !writing && (flags > 0 || reviewed);
  return {
    practice,
    ready,
    tourOpen: ready && !tourDone,
    reviewed: ready && reviewed,
    closeTour: () => {
      if (visitId) setPracticeTourDone(account, visitId, true);
      setTourDone(true);
    },
    restartTour: () => {
      if (visitId) setPracticeTourDone(account, visitId, false);
      setTourDone(false);
    },
  };
};
